import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import { PriceEstimatorDialog as PriceEstimatorPresentational } from './temp-presentational/PriceEstimatorDialog';
import { PremiumPlanIds } from '../../../constants/planIds';
import { formatCurrency } from '../../../utils/pricingUtils';
import MyAccountApi from '../api/MyAccountApi';
import { useUpgradeContext } from '../UpgradeContext';
import './PriceEstimator.scss';

const priceEstimatorDialogI18nKeys = {
  heading: 'upgrade-price-estimator.dialog-heading',
  ok: 'upgrade-price-estimator.ok',
  listSize: 'upgrade-price-estimator.table.header.list-size',
  plans: {
    GOOD: 'upgrade-price-estimator.plans.GOOD',
    BETTER: 'upgrade-price-estimator.plans.BETTER',
    LITE: 'upgrade-price-estimator.plans.LITE',
    GROWTH: 'upgrade-price-estimator.plans.GROWTH',
    BRONZE: 'upgrade-price-estimator.plans.BRONZE',
    SILVER: 'upgrade-price-estimator.plans.SILVER',
    GOLD: 'upgrade-price-estimator.plans.GOLD',
  },
  unknownPlanType: 'upgrade-price-estimator.plans.UNKNOWN',
  overageMessage: 'upgrade-price-estimator.overage-msg',
};

const PriceEstimatorDialog = ({ t, onClose, wrapClassName, planType: downgradePlanType }) => {
  const [planTiers, setPlanTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [upgradeContext] = useUpgradeContext();

  const { upgradeInfo, upgradeCurrency: currency } = upgradeContext;
  const { recommendedPlanType: recommendedPlanTypeTemp, recommendedPlanId } = upgradeInfo || {};

  let recommendedPlanType = recommendedPlanTypeTemp;

  if (!recommendedPlanType) {
    if (PremiumPlanIds.includes(recommendedPlanId)) recommendedPlanType = 'GOLD';
    else recommendedPlanType = 'SILVER';
  }

  const planType = recommendedPlanType || downgradePlanType;
  const currencyCode = currency || 'USD';

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const { plans } = await MyAccountApi.getPlanGroupWithGroup(1, 0, 0, upgradeInfo.recommendedPlanGroupId);
        const localPlan = plans.find((currentPlan) => currentPlan.type === planType) || { tiers: [] };
        const { type } = localPlan;
        let multiplier = 10;

        if (type === 'SILVER') {
          multiplier = 12;
        } else if (type === 'GOLD' || type === 'BETTER') {
          multiplier = 24;
        }

        setPlanTiers([
          {
            tier_min: t('upgrade-price-estimator.header-row'),
            tier_max: t('upgrade-price-estimator.header-allowance-row', { multiplier }),
            price: 0,
            headerRow: true,
          },
          ...localPlan.tiers,
        ]);
      } catch (error) {
        // handler error
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currencyCode, planType]);

  const renderTier = (tier, index) => {
    const { tier_min: min, tier_max: max, price, headerRow } = tier;
    const data = `${min.toLocaleString()} - ${max.toLocaleString()}`.replace(' - -1', '+');

    return (
      <PriceEstimatorPresentational.TierRow key={data} index={index}>
        <PriceEstimatorPresentational.ContactsCell>{headerRow ? min : data}</PriceEstimatorPresentational.ContactsCell>
        <PriceEstimatorPresentational.PricingCell>
          {headerRow ? max : formatCurrency(currencyCode, price)}
        </PriceEstimatorPresentational.PricingCell>
      </PriceEstimatorPresentational.TierRow>
    );
  };

  return (
    <PriceEstimatorPresentational
      isLoading={isLoading || !planTiers || planTiers.length === 0}
      className={wrapClassName}
      planType={planType}
      onClose={onClose}
      i18nKeys={priceEstimatorDialogI18nKeys}
    >
      {planTiers.map((data, index) => renderTier(data, index))}
    </PriceEstimatorPresentational>
  );
};

PriceEstimatorDialog.defaultProps = {
  wrapClassName: null,
  planType: null,
};

PriceEstimatorDialog.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  wrapClassName: PropTypes.string,
  planType: PropTypes.string,
};

export default PriceEstimatorDialog;
